import React, { useEffect, useMemo, useRef } from 'react'
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { StyledHourCell } from '../../../styles/LeaderCalendarTableStyles'
import { StyledTableHeadCell } from '../../../styles/TableStyles'
import { Mentee } from '../../../store/types/response/LeaderMenteesPlanResponse'
import AvatarComponent from '../../avatar/AvatarComponent'
import { DailyBar } from './DailyBar'
import { useNavigate } from 'react-router-dom'

type DailyCalendarTableProps = {
  selectedDate: string
  mentees: Mentee[]
  earliestHour: number
}

export const DailyCalendarTable = ({ selectedDate, mentees, earliestHour }: DailyCalendarTableProps) => {
  const navigate = useNavigate()
  const tableContainerRef = useRef<HTMLDivElement | null>(null)
  const hours = useMemo(
    () => Array.from({ length: 24 }, (_, i) => dayjs().hour(0).minute(0).add(i, 'hour').format('HH:00')),
    []
  )
  useEffect(() => {
    if (tableContainerRef.current) {
      if (earliestHour >= 8) tableContainerRef.current.scrollTop = 318
      else tableContainerRef.current.scrollTop = 318 - 45 * (8 - earliestHour)
    }
  }, [earliestHour])

  const navigateToUser = (id: string) => {
    navigate(`/users/${id}`)
  }

  const shortTimezone = new Date().toLocaleTimeString('en-us', { timeZoneName: 'short' }).split(' ')[2]

  return (
    <TableContainer ref={tableContainerRef} sx={{ maxHeight: 500, overflowY: 'auto', border: 'none', width: '100%' }}>
      <Table sx={{ borderCollapse: 'separate', borderSpacing: '0px' }}>
        <TableHead
          sx={{
            width: '100%',
            height: '34.29px',
            position: 'sticky',
            top: 0,
            zIndex: 5,
          }}
        >
          <TableRow>
            <StyledTableHeadCell
              align="center"
              sx={{
                top: '0px',
                width: '60px',
                position: 'sticky',
                left: 0,
                zIndex: 3,
                backgroundColor: 'background.elevation1',
                padding: 0,
              }}
            >
              <Box
                sx={{
                  width: '60px',
                  height: '34.29px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'background.elevation9',
                }}
              >
                {shortTimezone}
              </Box>
            </StyledTableHeadCell>

            {mentees.length === 0 ? (
              <TableCell
                sx={{
                  backgroundColor: 'background.elevation1',
                  border: 'none',
                }}
              ></TableCell>
            ) : (
              Array.from({ length: mentees.length }, (_, i) => (
                <StyledTableHeadCell
                  key={mentees[i].id}
                  align="center"
                  sx={{
                    flex: 1,
                    maxWidth: '210px',
                    minWidth: '210px',
                    top: '0px',
                    backgroundColor: 'background.elevation1',
                  }}
                >
                  <Stack
                    sx={{
                      width: '100%',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Tooltip title={`${mentees[i].lastName} ${mentees[i].firstName}`}>
                      <Stack
                        onClick={() => navigateToUser(mentees[i].id)}
                        sx={{
                          maxWidth: '210px',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: '5px',
                          cursor: 'pointer',
                        }}
                      >
                        <AvatarComponent
                          alt={`${mentees[i].firstName} ${mentees[i].lastName}`}
                          size={18}
                          avatar={mentees[i].avatar}
                          extension={mentees[i].extension}
                        />
                        <Typography
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '170px',
                            fontSize: 14,
                          }}
                        >
                          {mentees[i].lastName} {mentees[i].firstName}
                        </Typography>
                      </Stack>
                    </Tooltip>
                  </Stack>
                </StyledTableHeadCell>
              ))
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {hours.map(hour => (
            <TableRow key={hour}>
              <StyledHourCell key={'gap'}>
                <Box sx={{ marginTop: '-33px' }}>{hour !== '00:00' && hour}</Box>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: '92%',
                    width: '8%',
                    height: '1px',
                    backgroundColor: 'background.elevation4',
                  }}
                />
              </StyledHourCell>

              {mentees.length === 0 ? (
                <TableCell></TableCell>
              ) : (
                mentees.map(mentee => (
                  <DailyBar 
                    key={mentee.id} 
                    selectedDate={selectedDate} 
                    hour={hour} 
                    mentee={mentee} 
                    isWidened={mentees.length <= 2}
                  />
                ))
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}